/**
 * @param {{ haQa: object | null }} props
 */
export default function HumanAgentQaSection({ haQa }) {
  if (!haQa) return null;

  const agentRows = Array.isArray(haQa.agentRows) ? haQa.agentRows : [];

  return (
    <section className="mt-10 border-t border-slate-100 pt-8 dark:border-slate-800">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Agent quality (QA)</h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          QA runs automatically when agents resolve live-chat sessions. Scores reflect AI grading of human-agent
          replies in the selected date range.
        </p>
      </div>

      {haQa.isEmpty ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-8 text-center text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400">
          No human-agent QA data in this range. Resolve conversations with agent replies to generate scores.
        </div>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="ui-card">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">QA sessions</p>
              <p className="mt-2 text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                {haQa.totalAnalyzedSessions ?? 0}
              </p>
            </div>
            <div className="ui-card">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Workspace avg score</p>
              <p className="mt-2 text-2xl font-semibold tabular-nums text-teal-700 dark:text-teal-300">
                {Number(haQa.avgOverallScore ?? 0).toFixed(1)}/100
              </p>
            </div>
            <div className="ui-card">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Agents with QA</p>
              <p className="mt-2 text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                {haQa.agentCountWithQa ?? 0}
              </p>
            </div>
          </div>

          <div className="ui-card-static overflow-hidden !p-0">
            <div className="border-b border-slate-100 px-6 py-4 dark:border-slate-800">
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">Per-agent QA</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Sorted by average overall score</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px]">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80 text-left dark:border-slate-800 dark:bg-slate-900/50">
                    {["Agent", "QA sessions", "Overall", "Empathy", "Grammar"].map((h) => (
                      <th
                        key={h}
                        className="whitespace-nowrap px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {agentRows.map((a) => (
                    <tr key={a.id ?? a.name} className="bg-white/80 dark:bg-slate-950/40">
                      <td className="px-5 py-3.5 text-sm font-semibold text-slate-900 dark:text-slate-100">{a.name}</td>
                      <td className="px-5 py-3.5 text-sm tabular-nums text-slate-800 dark:text-slate-200">
                        {a.totalQaAnalyses}
                      </td>
                      <td className="px-5 py-3.5 text-sm font-semibold tabular-nums text-teal-700 dark:text-teal-300">
                        {a.avgOverallScore}/100
                      </td>
                      <td className="px-5 py-3.5 text-sm tabular-nums text-slate-600 dark:text-slate-400">
                        {a.avgEmpathy}/100
                      </td>
                      <td className="px-5 py-3.5 text-sm tabular-nums text-slate-600 dark:text-slate-400">
                        {a.avgGrammar}/100
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
